/**
 * StageTargetFieldView — pure presentation for the per-stage time-in-stage
 * target input (SE-5497). The draft state + commit live in the Container; this
 * View only renders the labelled numeric input and forwards its events.
 * @module features/player-kanban/components/molecules/StageTargetField/StageTargetFieldView
 */

import { memo } from "react";

import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";

import { Input } from "@/components/atoms/Input";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/** Props for {@link StageTargetFieldView}. */
export interface StageTargetFieldViewProps {
  /** The stage this field targets (drives the testID). */
  readonly stageId: string;
  /** Stage name (for the field's accessibility label). */
  readonly stageName: string;
  /** The current draft text. */
  readonly draft: string;
  /** Whether a write for this row is in flight (disables the input). */
  readonly isBusy: boolean;
  /** Draft text change. */
  readonly onDraftChange: (next: string) => void;
  /** Commit the current draft (blur / submit). */
  readonly onCommit: () => void;
}

/**
 * The per-stage time-target input.
 * @param root0 - {@link StageTargetFieldViewProps}.
 * @param root0.stageId - The stage this field targets.
 * @param root0.stageName - Stage name (for the field's accessibility label).
 * @param root0.draft - The current draft text.
 * @param root0.isBusy - Whether a write for this row is in flight.
 * @param root0.onDraftChange - Draft text change handler.
 * @param root0.onCommit - Commit handler (blur / submit).
 * @returns The labelled numeric input.
 */
const StageTargetFieldView = ({
  stageId,
  stageName,
  draft,
  isBusy,
  onDraftChange,
  onCommit,
}: StageTargetFieldViewProps) => (
  /* SE-6554: laid out on ONE line (label beside the input, not above it) so the
     restyled stage row stays a single-line card. Same field, same testID. */
  <Stack direction="horizontal" className="shrink-0 items-center gap-1">
    <Text variant="micro" className="text-content-muted">
      Target
    </Text>
    <Input
      className="w-14 rounded-sm"
      field={{
        value: draft,
        onChangeText: onDraftChange,
        onBlur: onCommit,
        onSubmitEditing: onCommit,
        keyboardType: "number-pad",
        placeholder: "—",
        editable: !isBusy,
        testID: TEST_ID_PLAYER_KANBAN.stageEditorTimeTargetFor(stageId),
        accessibilityLabel: `Time target in days for ${stageName}`,
      }}
    />
  </Stack>
);

StageTargetFieldView.displayName = "StageTargetFieldView";

export default memo(StageTargetFieldView);
